/**
 * Cliente NTVA Flow: manage (`/latest/manage`), consumo (`/latest/consumption`) y reportería.
 *
 * Variables de entorno:
 *   REACT_APP_NTVA_FLOW — base del API Gateway (sin slash final)
 *   REACT_APP_NTVA_FLOW_API_KEY — x-api-key
 *
 * Guardar/actualizar/borrar flow pasa por Gen 2 `/proxy/ntva-flow` (Cognito),
 * para que Auditoría tenga quién y cuándo. Con `REACT_APP_NUA_PROXY_URL`,
 * listar y GET también van al CDK (`/ntva/{hubSpotId}/latest/...`).
 */

import { fetchNtvaViaCdkOrDirect, proxyNtvaFlowManage } from './ntvaFlowProxy';

const getRoot = () => (process.env.REACT_APP_NTVA_FLOW || '').replace(/\/$/, '');
const getApiKey = () => process.env.REACT_APP_NTVA_FLOW_API_KEY || '';

const buildQueryString = (params) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ).toString();
  return qs ? `?${qs}` : '';
};

/** Query string para manage (permite valores vacíos como el API original). */
const manageBuildQueryString = (params) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null),
  ).toString();
  return qs ? `?${qs}` : '';
};

async function _fetchJson(baseUrl, method, path, body, hubSpotId) {
  const prefix = String(baseUrl || '').includes('/consumption')
    ? '/latest/consumption'
    : '/latest/manage';
  return fetchNtvaViaCdkOrDirect({
    prefix,
    method,
    path,
    body,
    hubSpotId,
    direct: async () => {
      if (!getRoot()) {
        throw new Error('Falta REACT_APP_NTVA_FLOW');
      }
      if (!getApiKey()) {
        // eslint-disable-next-line no-console
        console.warn(
          '[NuaTalkerNtvaFlow] Falta REACT_APP_NTVA_FLOW_API_KEY; las peticiones pueden devolver 403.',
        );
      }

      const res = await fetch(`${baseUrl}${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(getApiKey() ? { 'x-api-key': getApiKey() } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });

      const text = await res.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        const preview = (text || '').slice(0, 160).replace(/\s+/g, ' ');
        throw new Error(`Respuesta no JSON (HTTP ${res.status}). Inicio: "${preview}"`);
      }

      if (!res.ok) {
        const message = data?.message || data?.error || `HTTP ${res.status}`;
        const error = new Error(message);
        error.status = res.status;
        // Cuerpo completo del error (p. ej. la lista `errors` de una validación), igual que el proxy CDK.
        error.data = data;
        throw error;
      }

      return data;
    },
  });
}

/** Peticiones a `…/latest/consumption` (chat, reportería). */
export async function consumptionRequest(method, path, body, hubSpotId) {
  return _fetchJson(`${getRoot()}/latest/consumption`, method, path, body, hubSpotId);
}

/** Alias usado por el playground y el editor de flujo. */
export const flowConsumptionRequest = consumptionRequest;

/** Peticiones a `…/latest/manage` (flows, tools, KB, MCP). */
export async function managementRequest(method, path, body, hubSpotId) {
  return _fetchJson(`${getRoot()}/latest/manage`, method, path, body, hubSpotId);
}

// ─── Reportería (consumption) ─────────────────────────────────────────

const reportsChatBase = (clientId) => `/reports/chat/${encodeURIComponent(clientId)}`;

export function getReportingDashboard(clientId, params = {}) {
  return consumptionRequest('GET', `${reportsChatBase(clientId)}/dashboard${buildQueryString(params)}`);
}

export function getReportingConversations(clientId, params = {}) {
  const { nextToken, cursor, ...rest } = params;
  const token = cursor ?? nextToken;
  return consumptionRequest(
    'GET',
    `${reportsChatBase(clientId)}/conversations${buildQueryString({
      ...rest,
      ...(token ? { cursor: token } : {}),
    })}`,
  );
}

export function getReportingConversationTranscript(clientId, sessionId) {
  return consumptionRequest(
    'GET',
    `${reportsChatBase(clientId)}/conversations/${encodeURIComponent(sessionId)}`,
  );
}

export function getReportingSteps(clientId, params = {}) {
  return consumptionRequest('GET', `${reportsChatBase(clientId)}/steps${buildQueryString(params)}`);
}

export function getReportingFlowTransitions(clientId, params = {}) {
  return consumptionRequest('GET', `${reportsChatBase(clientId)}/flow-transitions${buildQueryString(params)}`);
}

export function getReportingEditorUsage(clientId, params = {}) {
  return consumptionRequest('GET', `${reportsChatBase(clientId)}/editor-usage${buildQueryString(params)}`);
}

/** Resumen de las encuestas de cierre: embudo, tendencia, comparaciones y resultados por pregunta. */
export function getReportingSurveys(clientId, params = {}) {
  return consumptionRequest('GET', `${reportsChatBase(clientId)}/surveys${buildQueryString(params)}`);
}

/** Corridas de encuestas (una por conversación), paginadas con `limit` y `offset`. */
export function getReportingSurveyRuns(clientId, params = {}) {
  return consumptionRequest('GET', `${reportsChatBase(clientId)}/surveys/runs${buildQueryString(params)}`);
}

// ─── Flow manage ───────────────────────────────────────────────────────

export const NTVA_FLOW_MANAGE_BASE_URL = `${getRoot()}/latest/manage`;

export const listFlows = (clientId, fields) =>
  managementRequest('GET', `/flow/${clientId}${manageBuildQueryString({ fields: fields?.join(',') })}`);

export const getFlow = (clientId, flowId, fields) =>
  managementRequest('GET', `/flow/${clientId}${manageBuildQueryString({ flowId, fields: fields?.join(',') })}`);

export const createFlow = (clientId, flowData) =>
  proxyNtvaFlowManage('POST', '/flow', { clientId, ...flowData });

export const updateFlow = (flowId, clientId, flowData) =>
  proxyNtvaFlowManage('PUT', '/flow', { flowId, clientId, ...flowData });

export const patchFlow = (flowId, clientId, fields) =>
  proxyNtvaFlowManage('PATCH', '/flow', { flowId, clientId, ...fields });

export const deleteFlow = (flowId, clientId) =>
  proxyNtvaFlowManage('DELETE', '/flow', { flowId, clientId });

export const listTools = (clientId, fields) =>
  managementRequest('GET', `/tool/${clientId}${manageBuildQueryString({ fields: fields?.join(',') })}`);

export const getTool = (clientId, toolId, fields) =>
  managementRequest('GET', `/tool/${clientId}${manageBuildQueryString({ toolId, fields: fields?.join(',') })}`);

export const createTool = (clientId, toolData) =>
  managementRequest('POST', '/tool', { clientId, ...toolData });

export const updateTool = (toolId, clientId, toolData) =>
  managementRequest('PUT', '/tool', { toolId, clientId, ...toolData });

export const patchTool = (toolId, clientId, fields) =>
  managementRequest('PATCH', '/tool', { toolId, clientId, ...fields });

export const deleteTool = (toolId, clientId) =>
  managementRequest('DELETE', '/tool', { toolId, clientId });

/** Catálogo de modelos LLM habilitados para el editor de auditores (tabla DynamoDB, no hardcoded). */
export const getAuditorModelOptions = () =>
  managementRequest('GET', '/auditor_model_options');

/** Mismo catálogo (tabla DynamoDB) pero con el scope del editor de flujos de chat. */
export const getChatFlowModelOptions = () =>
  managementRequest('GET', '/auditor_model_options?scope=chatFlow');

export const listKnowledgeBases = (clientId, fields) =>
  managementRequest(
    'GET',
    `/knowledge_base/${clientId}${manageBuildQueryString({ fields: fields?.join(',') })}`,
  );

export const getKnowledgeBase = (clientId, knowledgeBaseId, fields) =>
  managementRequest(
    'GET',
    `/knowledge_base/${clientId}${manageBuildQueryString({ knowledgeBaseId, fields: fields?.join(',') })}`,
  );

export const createKnowledgeBase = (clientId, payload) =>
  managementRequest('POST', '/knowledge_base', { clientId, ...payload });

export const updateKnowledgeBase = (knowledgeBaseId, clientId, kbData) =>
  managementRequest('PUT', '/knowledge_base', { knowledgeBaseId, clientId, ...kbData });

export const patchKnowledgeBase = (knowledgeBaseId, clientId, fields) =>
  managementRequest('PATCH', '/knowledge_base', { knowledgeBaseId, clientId, ...fields });

export const deleteKnowledgeBase = (knowledgeBaseId, clientId) =>
  managementRequest('DELETE', '/knowledge_base', { knowledgeBaseId, clientId });

/**
 * Pide URLs firmadas de S3 (una por archivo) para subir directo a la KB.
 * @param {string} knowledgeBaseId
 * @param {string} clientId
 * @param {Array<{ name: string, contentType: string }>} files
 * @returns {Promise<{ urls: Array<{ name: string, s3Key: string, uploadUrl: string, contentType: string, expiresIn: number }> }>}
 */
export const presignKbFiles = (knowledgeBaseId, clientId, files) =>
  managementRequest('POST', `/knowledge_base/file/presign?knowledgeBaseId=${knowledgeBaseId}`, {
    clientId,
    files,
  });

/**
 * Sube un archivo directo a S3 con una URL firmada (sin pasar por API Gateway/Lambda).
 * El Content-Type debe coincidir con el usado para firmar la URL.
 * @param {{ uploadUrl: string, contentType: string }} presigned
 * @param {File|Blob} file
 */
export const putFileToPresignedUrl = async (presigned, file) => {
  const res = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': presigned.contentType },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`No se pudo subir el archivo a S3 (HTTP ${res.status})`);
  }
};

/**
 * Estado de indexación RAG (ElevenLabs) de los documentos de una KB.
 * @returns {Promise<{ knowledgeBaseId, syncStatus, done, files: Record<string,{status,progressPercentage,durationSeconds}> }>}
 */
export const getKbRagStatus = (knowledgeBaseId, clientId) =>
  managementRequest(
    'GET',
    `/knowledge_base/rag_status?knowledgeBaseId=${encodeURIComponent(
      knowledgeBaseId,
    )}&clientId=${encodeURIComponent(clientId)}`,
  );

export const getKbFile = (knowledgeBaseId, clientId, fileName) =>
  managementRequest(
    'GET',
    `/knowledge_base/file?knowledgeBaseId=${encodeURIComponent(
      knowledgeBaseId,
    )}&clientId=${encodeURIComponent(clientId)}&fileName=${encodeURIComponent(fileName)}`,
  );

export const deleteKbFile = (knowledgeBaseId, clientId, fileName) =>
  managementRequest(
    'DELETE',
    `/knowledge_base/file?knowledgeBaseId=${encodeURIComponent(
      knowledgeBaseId,
    )}&fileName=${encodeURIComponent(fileName)}`,
    { clientId },
  );

// ─── Fuentes web de la KB (páginas y documentos enlazados) ────────────

/** Agrega URLs (páginas o documentos) y lanza la lectura. `parentId`: página donde se encontraron los documentos. */
export const addKbWebSources = (knowledgeBaseId, clientId, urls, parentId) =>
  managementRequest(
    'POST',
    `/knowledge_base/web?knowledgeBaseId=${encodeURIComponent(knowledgeBaseId)}`,
    { clientId, urls, ...(parentId ? { parentId } : {}) },
  );

/** Vuelve a leer todas las fuentes web o solo `urlIds`. */
export const crawlKbWebSources = (knowledgeBaseId, clientId, urlIds) =>
  managementRequest(
    'POST',
    `/knowledge_base/web/crawl?knowledgeBaseId=${encodeURIComponent(knowledgeBaseId)}`,
    { clientId, ...(urlIds?.length ? { urlIds } : {}) },
  );

/** Lista páginas del sitio de `url` (sus enlaces + sitemap por sección) para elegir cuáles agregar. No guarda nada. */
export const discoverKbWebPages = (knowledgeBaseId, clientId, url) =>
  managementRequest(
    'POST',
    `/knowledge_base/web/discover?knowledgeBaseId=${encodeURIComponent(knowledgeBaseId)}`,
    { clientId, url },
  );

/** Activa o apaga la actualización automática diaria de las páginas web de la base. No lanza lecturas. */
export const setKbWebAutoRefresh = (knowledgeBaseId, clientId, enabled) =>
  managementRequest(
    'POST',
    `/knowledge_base/web/auto_refresh?knowledgeBaseId=${encodeURIComponent(knowledgeBaseId)}`,
    { clientId, enabled },
  );

export const deleteKbWebSource = (knowledgeBaseId, clientId, urlId) =>
  managementRequest(
    'DELETE',
    `/knowledge_base/web?knowledgeBaseId=${encodeURIComponent(knowledgeBaseId)}&urlId=${encodeURIComponent(urlId)}`,
    { clientId },
  );

export const getHubSpotProperties = (clientId, objectType) =>
  managementRequest(
    'GET',
    `/hubspot_property/${clientId}${manageBuildQueryString({ objectType })}`,
  );

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const listMcps = (clientId, fields) =>
  managementRequest('GET', `/mcp/${clientId}${manageBuildQueryString({ fields: fields?.join(',') })}`);

export const getMcp = (clientId, mcpId, fields) =>
  managementRequest('GET', `/mcp/${clientId}${manageBuildQueryString({ mcpId, fields: fields?.join(',') })}`);

export const createMcp = (clientId, mcpData) =>
  managementRequest('POST', '/mcp', { clientId, ...mcpData });

export const updateMcp = (mcpId, clientId, mcpData) =>
  managementRequest('PUT', '/mcp', { mcpId, clientId, ...mcpData });

export const patchMcp = (mcpId, clientId, fields) =>
  managementRequest('PATCH', '/mcp', { mcpId, clientId, ...fields });

export const deleteMcp = (mcpId, clientId) =>
  managementRequest('DELETE', '/mcp', { mcpId, clientId });

// ─── Agendamiento (calendarios de Google) ────────────────────────────────

const schedulingBase = (clientId) => `/scheduling/${encodeURIComponent(clientId)}`;

export const listCalendars = (clientId) =>
  managementRequest('GET', `${schedulingBase(clientId)}/calendars`, undefined, clientId);

export const createCalendar = (clientId, calendar) =>
  managementRequest('POST', `${schedulingBase(clientId)}/calendars`, calendar, clientId);

export const updateCalendar = (clientId, calendarConfigId, calendar) =>
  managementRequest(
    'PUT',
    `${schedulingBase(clientId)}/calendars/${encodeURIComponent(calendarConfigId)}`,
    calendar,
    clientId,
  );

export const deleteCalendar = (clientId, calendarConfigId) =>
  managementRequest(
    'DELETE',
    `${schedulingBase(clientId)}/calendars/${encodeURIComponent(calendarConfigId)}`,
    undefined,
    clientId,
  );

export const testCalendarConnection = (clientId, calendarConfigId) =>
  managementRequest(
    'POST',
    `${schedulingBase(clientId)}/calendars/${encodeURIComponent(calendarConfigId)}/test`,
    {},
    clientId,
  );

export const getSchedulingServiceAccount = (clientId) =>
  managementRequest('GET', `${schedulingBase(clientId)}/service_account`, undefined, clientId);

export const getGoogleConfig = (clientId) =>
  managementRequest('GET', `${schedulingBase(clientId)}/google/config`, undefined, clientId);

export const connectGoogle = (clientId, code) =>
  managementRequest('POST', `${schedulingBase(clientId)}/google/connect`, { code }, clientId);

export const listGoogleCalendars = (clientId, connectionId) =>
  managementRequest(
    'GET',
    `${schedulingBase(clientId)}/google/connections/${encodeURIComponent(connectionId)}/calendars`,
    undefined,
    clientId,
  );

// ─── Encuestas de cierre (administrador) ────────────────────────────────

/** Lista las encuestas del cliente. */
export const listSurveys = (clientId, fields) =>
  managementRequest('GET', `/survey/${clientId}${manageBuildQueryString({ fields: fields?.join(',') })}`);

/** Obtiene una encuesta específica. */
export const getSurvey = (clientId, surveyId, fields) =>
  managementRequest('GET', `/survey/${clientId}${manageBuildQueryString({ surveyId, fields: fields?.join(',') })}`);

export const createSurvey = (clientId, surveyData) =>
  managementRequest('POST', '/survey', { clientId, ...surveyData });

/** Reemplaza el contenido completo de la encuesta. */
export const updateSurvey = (surveyId, clientId, surveyData) =>
  managementRequest('PUT', '/survey', { surveyId, clientId, ...surveyData });

export const deleteSurvey = (surveyId, clientId) =>
  managementRequest('DELETE', '/survey', { surveyId, clientId });

/**
 * Un turno de la prueba de una encuesta en borrador (playground). Corre el motor real sin guardar la
 * encuesta y sin escribir en HubSpot. El primer turno envía `[START_SURVEY]`; los siguientes devuelven el
 * `surveyState` del anterior.
 */
export const previewSurveyTurn = (body) => consumptionRequest('POST', '/chat/survey-preview', body);

// ─── Planes / Entitlements ───────────────────────────────────────────────

/** Catalogo de planes (admin): listar / crear / editar / eliminar.
 *  NTVA `/plan` no lleva tenant; `clientId` solo autentica el proxy CDK. */
export const listPlans = (clientId) =>
  managementRequest('GET', '/plan', undefined, clientId);

export const createPlan = (plan, clientId) =>
  managementRequest('POST', '/plan', plan, clientId);

export const updatePlan = (planId, plan, clientId) =>
  managementRequest('PUT', '/plan', { ...plan, planId }, clientId);

export const deletePlan = (planId, clientId) =>
  managementRequest('DELETE', '/plan', { planId }, clientId);

/** Entitlements efectivos de un cliente (resuelve plan o custom; default ilimitado). */
export const getEntitlements = (clientId) =>
  managementRequest('GET', `/entitlement/${encodeURIComponent(clientId)}`);

/** Asigna un plan de catalogo al cliente (snapshot + materializa bolsas). */
export const assignPlan = (clientId, planId) =>
  managementRequest('POST', '/entitlement/assign', { clientId, planId });

/** Guarda entitlements personalizados (custom) y re-materializa bolsas. */
export const setCustomEntitlements = (clientId, entitlements) =>
  managementRequest('PUT', '/entitlement', { clientId, entitlements });

class NuaTalkerNtvaFlowService {
  constructor() {
    this.endpoint = process.env.REACT_APP_NTVA_FLOW;
    this.apiKey = process.env.REACT_APP_NTVA_FLOW_API_KEY;
  }

  async getFlows(hubSpotId) {
    return listFlows(hubSpotId);
  }
}

export default NuaTalkerNtvaFlowService;
